(() => {
  const SELECTOR = '[data-rd-lehenga-hero]';

  function initHero(root) {
    if (!root || root.dataset.rdInitialized === 'true') return;
    root.dataset.rdInitialized = 'true';

    let variants = [];
    try {
      variants = JSON.parse(root.querySelector('[data-rd-variants]')?.textContent || '[]');
    } catch (e) {
      variants = [];
    }

    // Full product titles wrap naturally; do not measure or force an inline font-size.

    const track = root.querySelector('[data-rd-track]');
    const stage = root.querySelector('[data-rd-stage]');
    const slides = Array.from(root.querySelectorAll('[data-rd-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-rd-thumb]'));
    const thumbViewport = root.querySelector('[data-rd-thumb-viewport]');
    const currentEl = root.querySelector('[data-rd-current]');
    let index = Math.max(0, Math.min(Number(track?.dataset.activeIndex || 0), Math.max(slides.length - 1, 0)));
    let startX = 0;
    let deltaX = 0;
    let dragging = false;
    const galleryMobileQuery = window.matchMedia('(max-width: 989px)');
    let observedGalleryWidth = 0;

    // Use the loaded image's actual dimensions first. Shopify's preview_image aspect
    // ratio is only an initial fallback (it may differ from the delivered image).
    const activeMediaRatio = () => {
      const slide = slides[index];
      if (!slide) return 0.8;
      const image = slide.querySelector('img.rd-lh-gallery__image');
      if (image?.naturalWidth > 0 && image?.naturalHeight > 0) {
        return image.naturalWidth / image.naturalHeight;
      }
      const ratio = Number(slide.dataset.rdAspect);
      return Number.isFinite(ratio) && ratio > 0 ? ratio : 0.8;
    };

    const sizeGalleryToActiveMedia = () => {
      if (!stage) return;
      if (!galleryMobileQuery.matches) {
        // Preserve the existing fixed-height desktop gallery precisely.
        stage.style.removeProperty('height');
        stage.style.removeProperty('aspect-ratio');
        return;
      }

      const width = stage.getBoundingClientRect().width;
      if (width < 10) return; // Hidden in the editor: retry when visible/resized.
      const ratio = activeMediaRatio();
      const configured = parseFloat(getComputedStyle(root).getPropertyValue('--rd-lh-gallery-mobile-max'));
      const maxSetting = Number.isFinite(configured) && configured > 0 ? configured : 900;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
      const upperLimit = Math.max(180, Math.min(maxSetting, viewportHeight * 0.92));
      const lowerLimit = Math.min(220, upperLimit);
      // Extra-wide or extra-tall files fit INSIDE the frame via object-fit: contain.
      const height = Math.round(Math.max(lowerLimit, Math.min(width / ratio, upperLimit)));
      stage.style.height = `${height}px`;
      stage.style.aspectRatio = 'auto';
    };

    const setActive = (nextIndex, options = {}) => {
      if (!track || !slides.length) return;
      index = Math.max(0, Math.min(nextIndex, slides.length - 1));
      sizeGalleryToActiveMedia();
      track.style.transform = `translate3d(-${index * 100}%,0,0)`;
      track.dataset.activeIndex = String(index);
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (currentEl) currentEl.textContent = String(index + 1);
      if (!options.skipThumbScroll && thumbs[index] && thumbViewport) {
        const rect = thumbViewport.getBoundingClientRect();
        const thumbRect = thumbs[index].getBoundingClientRect();
        const behavior = options.instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const vertical = thumbViewport.scrollHeight > thumbViewport.clientHeight + 2;
        const gap = 5;
        if (vertical) {
          let delta = 0;
          if (thumbRect.top < rect.top + gap) delta = thumbRect.top - rect.top - gap;
          else if (thumbRect.bottom > rect.bottom - gap) delta = thumbRect.bottom - rect.bottom + gap;
          if (Math.abs(delta) > 1) thumbViewport.scrollTo({ top: thumbViewport.scrollTop + delta, behavior });
        } else {
          let delta = 0;
          if (thumbRect.left < rect.left + gap) delta = thumbRect.left - rect.left - gap;
          else if (thumbRect.right > rect.right - gap) delta = thumbRect.right - rect.right + gap;
          if (Math.abs(delta) > 1) thumbViewport.scrollTo({ left: thumbViewport.scrollLeft + delta, behavior });
        }
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => setActive(Number(thumb.dataset.index || 0)));
    });
    root.querySelector('[data-rd-prev]')?.addEventListener('click', () => setActive(index - 1));
    root.querySelector('[data-rd-next]')?.addEventListener('click', () => setActive(index + 1));


    if (stage && track && slides.length > 1) {
      stage.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button, a, video, model-viewer, iframe')) return;
        dragging = true;
        startX = event.clientX;
        deltaX = 0;
        track.classList.add('is-dragging');
        stage.setPointerCapture?.(event.pointerId);
      });
      stage.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        deltaX = event.clientX - startX;
        const pct = (deltaX / Math.max(stage.clientWidth, 1)) * 100;
        track.style.transform = `translate3d(calc(-${index * 100}% + ${pct}%),0,0)`;
      });
      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        const threshold = Math.min(100, Math.max(45, stage.clientWidth * 0.12));
        if (Math.abs(deltaX) >= threshold) setActive(index + (deltaX < 0 ? 1 : -1));
        else setActive(index, { skipThumbScroll: true });
        deltaX = 0;
      };
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);
      stage.addEventListener('lostpointercapture', endDrag);
    }

    const syncOverlayState = () => {
      const visibleOverlay = Array.from(root.querySelectorAll('[data-rd-callback-modal], [data-rd-size-guide-modal], [data-rd-lightbox]'))
        .some((modal) => !modal.hidden);
      // Keep the page unlocked when all product-hero overlays are closed.
      document.body.classList.toggle('rd-lh-modal-open', visibleOverlay);
      root.dispatchEvent(new CustomEvent('rd:overlay-change', { bubbles: true, detail: { open: visibleOverlay } }));
    };

    const currentVariant = () =>
      variants.find((variant) => String(variant.id) === String(root.dataset.selectedVariantId)) || variants[0];

    const findVariantForSize = (sizeValue, optionIndex) => {
      const selected = currentVariant();
      if (!selected || !Array.isArray(selected.options)) return null;
      const requestedOptions = [...selected.options];
      requestedOptions[optionIndex] = sizeValue;
      return variants.find((variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === requestedOptions.length &&
        variant.options.every((value, i) => String(value) === String(requestedOptions[i]))
      ) || null;
    };

    const sizePickers = Array.from(root.querySelectorAll('[data-rd-size-picker]'));

    const syncSizeButtons = (variant) => {
      if (!variant) return;
      sizePickers.forEach((picker) => {
        const optionIndex = Number(picker.dataset.optionIndex || 0);
        const value = String(variant.options?.[optionIndex] ?? '');
        picker.querySelectorAll('[data-rd-size-value]').forEach((button) => {
          const chosen = String(button.dataset.rdSizeValue) === value;
          const valid = !!findVariantForSize(button.dataset.rdSizeValue || '', optionIndex);
          button.classList.toggle('is-selected', chosen);
          button.classList.toggle('is-unavailable', !valid);
          button.setAttribute('aria-pressed', String(chosen));
          button.disabled = !valid;
          if (!valid) button.setAttribute('title', 'Not available with current options');
          else button.removeAttribute('title');
        });
      });
    };

    const updateVariant = (variant, options = {}) => {
      if (!variant) return false;
      root.dataset.selectedVariantId = String(variant.id);

      const price = root.querySelector('[data-rd-price]');
      const compare = root.querySelector('[data-rd-compare]');
      const skuWrap = root.querySelector('[data-rd-sku]');
      const skuValue = root.querySelector('[data-rd-sku-value]');
      if (price) price.textContent = variant.price_formatted || '';
      if (compare) {
        const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        compare.textContent = showCompare ? (variant.compare_at_price_formatted || '') : '';
        compare.classList.toggle('is-hidden', !showCompare);
      }
      if (skuValue) skuValue.textContent = variant.sku || '';
      if (skuWrap) skuWrap.classList.toggle('is-empty', !variant.sku);

      root.querySelectorAll('[data-rd-lead-variant-id]').forEach((field) => { field.value = String(variant.id || ''); });
      root.querySelectorAll('[data-rd-lead-sku]').forEach((field) => { field.value = variant.sku || ''; });

      const sizePicker = sizePickers[0];
      const sizeIndex = sizePicker ? Number(sizePicker.dataset.optionIndex || 0) : -1;
      const sizeValue = sizeIndex >= 0 ? String(variant.options?.[sizeIndex] ?? '') : '';
      root.querySelectorAll('[data-rd-lead-size]').forEach((field) => {
        // The option is selected from the verified product variant, not from an unvalidated click.
        if (field.tagName === 'SELECT') {
          if (Array.from(field.options).some((opt) => opt.value === sizeValue)) field.value = sizeValue;
        } else field.value = sizeValue;
      });
      syncSizeButtons(variant);

      if (variant.featured_media_id && !options.skipMedia) {
        const mediaIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.featured_media_id));
        if (mediaIndex >= 0) setActive(mediaIndex);
      }

      if (!options.skipHistory) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) { /* URL state is not essential to the lead form. */ }
      }

      root.dispatchEvent(new CustomEvent('rd:variant-change', {
        bubbles: true, detail: { variant, size: sizeValue }
      }));
      return true;
    };

    const initialVariant = currentVariant();
    if (initialVariant) updateVariant(initialVariant, { skipHistory: true, skipMedia: true });

    sizePickers.forEach((picker) => {
      const optionIndex = Number(picker.dataset.optionIndex || 0);
      picker.querySelectorAll('[data-rd-size-value]').forEach((button) => {
        button.addEventListener('click', () => {
          const sizeValue = button.dataset.rdSizeValue || '';
          const match = findVariantForSize(sizeValue, optionIndex);
          if (!match) {
            // Never submit a lead size that disagrees with its variant ID/SKU.
            syncSizeButtons(currentVariant());
            root.dispatchEvent(new CustomEvent('rd:size-rejected', { bubbles: true, detail: { requested: sizeValue } }));
            return;
          }
          updateVariant(match);
        });
      });
    });

    root.querySelectorAll('[data-rd-size-picker]').forEach((sizePicker) => {
      const modal = sizePicker.querySelector('[data-rd-size-guide-modal]');
      const open = sizePicker.querySelector('[data-rd-size-guide-open]');
      const closes = sizePicker.querySelectorAll('[data-rd-size-guide-close]');
      if (!modal || !open) return;

      const openSizeGuide = () => {
        modal.hidden = false;
        syncOverlayState();
        requestAnimationFrame(() => modal.querySelector('.rd-lh-size-guide__panel')?.focus());
      };
      const closeSizeGuide = () => {
        modal.hidden = true;
        syncOverlayState();
        open.focus();
      };

      open.addEventListener('click', openSizeGuide);
      closes.forEach((button) => button.addEventListener('click', closeSizeGuide));
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !modal.hidden) closeSizeGuide();
      });
    });

    root.querySelectorAll('[data-rd-callback]').forEach((callback) => {
      const modal = callback.querySelector('[data-rd-callback-modal]');
      const open = callback.querySelector('[data-rd-callback-open]');
      const closes = callback.querySelectorAll('[data-rd-callback-close]');
      if (!modal || !open) return;

      const openModal = () => {
        modal.hidden = false;
        syncOverlayState();
        requestAnimationFrame(() => modal.querySelector('input:not([type="hidden"])')?.focus());
      };
      const closeModal = () => {
        modal.hidden = true;
        syncOverlayState();
        open.focus();
      };
      open.addEventListener('click', openModal);
      closes.forEach((btn) => btn.addEventListener('click', closeModal));
      callback.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !modal.hidden) closeModal();
      });
      if (callback.querySelector('[data-rd-callback-success]') || callback.querySelector('.rd-lh-callback__errors')) openModal();
    });

    root.querySelectorAll('[data-rd-review-ticker]').forEach((ticker) => {
      const items = Array.from(ticker.querySelectorAll('[data-rd-review-item]'));
      if (items.length <= 1) return;

      let reviewIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
      let timer = null;
      const speed = Math.max(2000, Number(ticker.dataset.speed || 4000));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const showReview = (nextIndex) => {
        const current = items[reviewIndex];
        const next = items[nextIndex];
        if (!next || next === current) return;

        current?.classList.add('is-leaving');
        current?.classList.remove('is-active');
        current?.setAttribute('aria-hidden', 'true');

        next.classList.remove('is-leaving');
        next.classList.add('is-active');
        next.setAttribute('aria-hidden', 'false');

        if (current) window.setTimeout(() => current.classList.remove('is-leaving'), reducedMotion ? 0 : 460);
        reviewIndex = nextIndex;
      };

      const startTicker = () => {
        if (reducedMotion || timer) return;
        timer = window.setInterval(() => showReview((reviewIndex + 1) % items.length), speed);
      };
      const stopTicker = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      };

      ticker.addEventListener('pointerenter', stopTicker);
      ticker.addEventListener('pointerleave', startTicker);
      ticker.addEventListener('focusin', stopTicker);
      ticker.addEventListener('focusout', startTicker);
      startTicker();
    });

    const lightbox = root.querySelector('[data-rd-lightbox]');
    const lightboxImg = lightbox?.querySelector('[data-rd-lightbox-image]');
    if (lightbox && lightboxImg) {
      root.querySelectorAll('[data-rd-zoom]').forEach((button) => {
        button.addEventListener('click', () => {
          lightboxImg.src = button.dataset.zoomSrc || '';
          lightboxImg.alt = button.dataset.zoomAlt || '';
          lightbox.hidden = false;
          syncOverlayState();
          lightbox.querySelector('[data-rd-lightbox-close]')?.focus();
        });
      });
      lightbox.querySelectorAll('[data-rd-lightbox-close]').forEach((button) => {
        button.addEventListener('click', () => {
          lightbox.hidden = true;
          lightboxImg.src = '';
          syncOverlayState();
        });
      });
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !lightbox.hidden) {
          lightbox.hidden = true;
          lightboxImg.src = '';
          syncOverlayState();
        }
      });
    }

    // Re-measure once an image has loaded; the original dimensions may not match
    // a Shopify thumbnail's metadata or the image could load from cache later.
    slides.forEach((slide, slideIndex) => {
      const image = slide.querySelector('img.rd-lh-gallery__image');
      if (!image) return;
      image.addEventListener('load', () => {
        if (slideIndex === index) sizeGalleryToActiveMedia();
      });
    });
    const onGalleryResize = () => sizeGalleryToActiveMedia();
    window.addEventListener('resize', onGalleryResize, { passive: true });
    // Observe width changes from desktop column resizing or Shopify editor panes.
    if ('ResizeObserver' in window && stage) {
      observedGalleryWidth = stage.getBoundingClientRect().width;
      const galleryObserver = new ResizeObserver(() => {
        const width = stage.getBoundingClientRect().width;
        if (Math.abs(width - observedGalleryWidth) > 1) {
          observedGalleryWidth = width;
          sizeGalleryToActiveMedia();
        }
      });
      galleryObserver.observe(stage);
      root.addEventListener('shopify:section:unload', () => galleryObserver.disconnect(), { once: true });
    }
    syncOverlayState();
    setActive(index, { instant: true, skipThumbScroll: true });
  }

  const initAll = (scope = document) => {
    if (scope.matches?.(SELECTOR)) initHero(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initHero);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
